// Pano yardımcıları (saf). Sütun: { status, tasks: TaskSummary[] } — tasks _id taşır.

/**
 * Kartı hedef sütuna taşır; yeni sırayı beforeId/afterId komşularına göre bulur.
 * Girdiyi değiştirmez. Bilinmeyen iş ise columns aynen döner.
 * beforeId = kartın üstündeki komşu, afterId = altındaki komşu (ikisi de yoksa sütun sonu).
 */
export function applyMoveToColumns(columns, { taskId, status, beforeId = null, afterId = null }) {
  if (!Array.isArray(columns)) return columns;
  let moving = null;
  for (const col of columns) {
    const found = (col.tasks || []).find((t) => t._id === taskId);
    if (found) {
      moving = found;
      break;
    }
  }
  if (!moving) return columns;

  const next = columns.map((col) => ({
    ...col,
    tasks: (col.tasks || []).filter((t) => t._id !== taskId),
  }));
  const target = next.find((col) => col.status === status);
  if (!target) return columns;

  const card = { ...moving, status };
  let index = target.tasks.length;
  const beforeIdx = beforeId ? target.tasks.findIndex((t) => t._id === beforeId) : -1;
  const afterIdx = afterId ? target.tasks.findIndex((t) => t._id === afterId) : -1;
  if (beforeIdx >= 0) index = beforeIdx + 1;
  else if (afterIdx >= 0) index = afterIdx;
  else if (!beforeId && afterId) index = 0;
  target.tasks = [...target.tasks.slice(0, index), card, ...target.tasks.slice(index)];
  return next;
}

// move yanıtındaki (TaskSummary) sıra/sürüm alanları. Pano kartı bunlarla güncellenir ki
// sonraki sürükleme güncel version ile gitsin (409 olmasın).
export const MOVE_FIELDS = ["version", "boardOrder", "status", "completedAt"];
export const pickMoveFields = (task) =>
  MOVE_FIELDS.reduce((acc, k) => (task && k in task ? { ...acc, [k]: task[k] } : acc), {});

/** Sunucunun döndürdüğü taşınmış işin alanlarını sütunlardaki karta yazar. Girdiyi değiştirmez. */
export function mergeMovedTask(columns, task) {
  if (!Array.isArray(columns) || !task?._id) return columns;
  const patch = pickMoveFields(task);
  let hit = false;
  const next = columns.map((col) => {
    const idx = (col.tasks || []).findIndex((t) => t._id === task._id);
    if (idx < 0) return col;
    hit = true;
    const tasks = [...col.tasks];
    tasks[idx] = { ...tasks[idx], ...patch };
    return { ...col, tasks };
  });
  return hit ? next : columns;
}

export const COLUMN_PREFIX = "column:";

/**
 * Sürükle-bırak sonucunda hedef sütun ve komşu id'lerini hesaplar.
 * overId: kart id'si veya "column:<status>" (sütun boşluğu/sonu → sütun sonuna ekle).
 * beforeId = kartın üstünde kalacak komşu, afterId = altında kalacak komşu
 * (aktif kart çıkarıldıktan sonraki listeye göre). Konum değişmiyorsa null döner.
 * placeBelow: overId bir kartsa ve true ise kart hedefin ALTINA eklenir.
 */
export function computeDropTarget(columns, activeId, overId, { placeBelow = false } = {}) {
  if (!Array.isArray(columns) || !activeId || !overId || activeId === overId) return null;
  const fromCol = columns.find((c) => (c.tasks || []).some((t) => t._id === activeId));
  if (!fromCol) return null;

  let toCol;
  let overIndex = -1;
  if (String(overId).startsWith(COLUMN_PREFIX)) {
    toCol = columns.find((c) => c.status === String(overId).slice(COLUMN_PREFIX.length));
  } else {
    toCol = columns.find((c) => (c.tasks || []).some((t) => t._id === overId));
    if (toCol) overIndex = toCol.tasks.findIndex((t) => t._id === overId);
  }
  if (!toCol) return null;

  const fromIndex = fromCol.tasks.findIndex((t) => t._id === activeId);
  const rest = toCol.tasks.filter((t) => t._id !== activeId);
  // Aynı sütunda arrayMove semantiği (aşağı → hedefin altı, yukarı → hedefin üstü); sütunlar arası → hedefin önüne.
  let index = rest.length;
  if (overIndex >= 0) {
    // placeBelow → hedef kartın altına. Aksi halde aynı sütunda aşağı taşımada rest'te hedef bir öne kayar;
    // overIndex hedefin ALTINA denk gelir (arrayMove), yukarı/sütunlar arası → hedefin önüne.
    const restOver = rest.findIndex((t) => t._id === overId);
    index = placeBelow ? restOver + 1 : overIndex;
  }
  if (fromCol === toCol && index === fromIndex) return null;
  return {
    status: toCol.status,
    beforeId: index > 0 ? rest[index - 1]._id : null,
    afterId: index < rest.length ? rest[index]._id : null,
  };
}

/**
 * Sıralamasız pano (tüm projeler, contract 1.3): yalnız sütun değişimi geçerlidir.
 * Aynı sütuna bırakma null; başka sütuna bırakma komşusuz hedef (sütun sonu).
 */
export function columnOnlyTarget(columns, activeId, drop) {
  if (!drop || !Array.isArray(columns)) return null;
  const from = columns.find((c) => (c.tasks || []).some((t) => t._id === activeId));
  if (!from || from.status === drop.status) return null;
  return { status: drop.status, beforeId: null, afterId: null };
}

export const GHOST_PREFIX = "ghost:";

/**
 * Kart içi alt işler (Client#127): her sütunda, üst işinin kartı AYNI sütundaysa alt iş
 * o kartın gövdesine girer. Üst iş başka sütundaysa (veya panoda yoksa) alt iş o sütunda
 * üst işin GÖLGE kartının gövdesinde durur — üst işli görünüm her sütunda korunur.
 * Gölge kart sürüklenmez, sütun sayısına girmez. Girdiyi değiştirmez.
 * → sütun başına { status, tasks, top, nested }
 *   top: sütunda kart olarak çizilenler (sunucu sırası); gölge girişi
 *        { _id: "ghost:<üstId>", ghost: true, parent, project, parentStatus } ilk alt işinin yerinde,
 *   nested: Map(üstId → alt iş kartları, sunucu sırası),
 *   tasks: görsel düz sıra (her üst kart, ardından gövdesindeki alt işler; gölge kartın
 *          yalnız alt işleri) — sürükle-bırak komşuları ekranda görünen sıraya göre
 *          hesaplansın diye.
 */
export function nestColumns(columns) {
  if (!Array.isArray(columns)) return [];
  const statusById = new Map();
  columns.forEach((col) => (col.tasks || []).forEach((t) => statusById.set(t._id, col.status)));
  return columns.map((col) => {
    const tasks = col.tasks || [];
    const ids = new Set(tasks.map((t) => t._id));
    const hasChildHere = new Set(tasks.map((t) => t.parent?._id).filter((pid) => pid && ids.has(pid)));
    const nested = new Map();
    const top = [];
    for (const t of tasks) {
      const pid = t.parent?._id;
      // Kendi alt işleri bu sütunda olan iş gölgeye girmez — onları gövdesinde taşır.
      if (!pid || hasChildHere.has(t._id)) {
        top.push(t);
        continue;
      }
      if (!nested.has(pid)) {
        nested.set(pid, []);
        if (!ids.has(pid)) {
          top.push({
            _id: `${GHOST_PREFIX}${pid}`,
            ghost: true,
            parent: t.parent,
            project: t.project,
            parentStatus: statusById.get(pid) || null,
          });
        }
      }
      nested.get(pid).push(t);
    }
    const flat = top.flatMap((t) =>
      t.ghost ? nested.get(t.parent._id) : [t, ...(nested.get(t._id) || [])]
    );
    return { ...col, tasks: flat, top, nested };
  });
}

/**
 * Üst iş başka sütuna taşınınca onunla birlikte gidecek alt işler: kartın
 * gövdesinde duranlar, yani üst işle AYNI sütundakiler. Başka sütundaki (gölge
 * kartta duran) alt işler ve düzenleme yetkisi olmayanlar yerinde kalır.
 * Aynı sütun içinde sıra değişiminde ya da alt iş sürüklenirken boş döner.
 * → taşıma istekleri (sunucu sırası korunur): her alt iş bir öncekinin hemen
 *   altına, hedefteki alt komşunun (drop.afterId) üstüne yerleşir.
 */
export function subtaskMovesWith(columns, taskId, drop) {
  if (!Array.isArray(columns) || !drop) return [];
  const fromCol = columns.find((c) => (c.tasks || []).some((t) => t._id === taskId));
  if (!fromCol || fromCol.status === drop.status) return [];
  const parent = fromCol.tasks.find((t) => t._id === taskId);
  if (parent?.parent?._id) return [];
  const followers = fromCol.tasks.filter(
    (t) => t.parent?._id === taskId && t.can?.edit !== false
  );
  const reorderable = Boolean(drop.beforeId || drop.afterId);
  let prev = taskId;
  return followers.map((t) => {
    const move = {
      id: t._id,
      version: t.version,
      status: drop.status,
      beforeId: reorderable ? prev : null,
      afterId: reorderable ? drop.afterId || null : null,
    };
    prev = t._id;
    return move;
  });
}
